import {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {ORDER_SORT_DIR, parsePlanTab, PlanCifras, PlanMando, type PlanOrder} from '@/components/plan-mando'
import {PlanSugerenciasNotices} from '@/components/analisis/analisis-sugerencia-aviso'
import {useAnalisisSugerencias} from '@/hooks/sgsst/plan/use-analisis-causa'
import {sgsstPageGuard} from '@/components/no-access-state'
import {PlanLockedPage} from '@/components/plan-upsell'
import {PlanDialogos, type Analizando} from '@/components/plan-dialogos'
import {PlanEncabezado} from '@/components/plan-encabezado'
import {PlanFlatList} from '@/components/plan-flat-list'
import {computeGroupRisk, PlanListaAgrupada, type PlanGroup} from '@/components/plan-grupos'
import {PlanTarjetaVacia, PlanVacio} from '@/components/plan-vacio'
import {PlanResumenGerencial} from '@/components/plan-resumen-gerencial'
import {useResponsablesRoster} from '@/hooks/empresa/identidad/use-employees'
import {useEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  useActions,
  useActionsFlat,
  useActionsSearchIds,
  usePlanResumen,
  type FuentePlan,
  type SgsstAction,
} from '@/hooks/sgsst/plan/use-actions'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {useHighlightParam} from '@/hooks/common/use-highlight-param'
import {usePlanPropuesta} from '@/hooks/sgsst/plan/use-plan-propuesta'
import {useResolveItem, useSgsstPage, type SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {addCop, ZERO_COP, type Cop} from '@/lib/money'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'

// Viaja en `?tab=` para que un enlace compartido abra donde el que lo mandó estaba mirando.
type PlanTab = 'plan' | 'resumen'

export function SgsstPlanPage() {
  const {t} = useTranslation()
  const {businessId, hash, viewedYear, business, capabilities, canOperate, state, godReadOnly, loading} = useSgsstPage()
  const navigate = useNavigate()
  const {data: actionsPage} = useActions(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  const [formOpen, setFormOpen] = useState(false)
  // El lado externo del roster solo se pide mientras `ActionFormDialog` está abierto.
  const {data: roster} = useResponsablesRoster(canOperate ? businessId : undefined, formOpen)
  const {data: yearEvidence} = useEvidence(canOperate && state?.assessment ? businessId : undefined, viewedYear)
  const resolveItem = useResolveItem(businessId!)
  const [formStandard, setFormStandard] = useState<SgsstItem | null>(null)
  const [formSuggestedTitulo, setFormSuggestedTitulo] = useState<string | null>(null)
  const [editing, setEditing] = useState<SgsstAction | null>(null)
  const [deleting, setDeleting] = useState<SgsstAction | null>(null)
  const [notesAction, setNotesAction] = useState<SgsstAction | null>(null)
  const [analizando, setAnalizando] = useState<Analizando | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [sortMode, setSortMode] = useState<'estandar' | 'riesgo'>('estandar')
  // Filtro solo de vista: la barra de progreso y la exposición siguen sobre todas las acciones.
  const [statusFilter, setStatusFilter] = useState<'todas' | 'pendientes'>('todas')
  const [order, setOrder] = useState<PlanOrder>('agrupado')
  const [searchParams, setSearchParams] = useSearchParams()
  // En la URL para compartir el enlace y que «atrás» deshaga el filtro. Un filtro aplana la lista: la
  // vista agrupada calcula avance y dinero sobre el conjunto completo.
  const fuente = (searchParams.get('fuente') as FuentePlan | null) ?? null
  const mesParam = searchParams.get('mes')
  const mes = mesParam === null ? null : Number(mesParam)
  const filtrado = fuente !== null || mes !== null
  const flattened = order !== 'agrupado' || filtrado
  const ordenEfectivo: PlanOrder = filtrado && order === 'agrupado' ? 'target_date' : order
  const [flatPage, setFlatPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)
  function chooseOrder(next: PlanOrder) {
    setOrder(next)
    setFlatPage(1)
  }
  function setFiltro(filtro: {fuente?: FuentePlan; mes?: number}) {
    setFlatPage(1)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', 'plan')
        for (const [clave, valor] of [
          ['fuente', filtro.fuente],
          ['mes', filtro.mes],
        ] as const) {
          if (valor === undefined) params.delete(clave)
          else params.set(clave, String(valor))
        }
        return params
      },
      {replace: true}
    )
  }
  function updateSearch(value: string) {
    setSearchInput(value)
    setFlatPage(1)
  }

  const assessment = state?.assessment
  const actions = useMemo(() => actionsPage?.items ?? [], [actionsPage])
  // `useActions` pide el page_size máximo del backend; si el total lo supera se dice, no se trunca en silencio.
  const actionsTruncated = !!actionsPage && actionsPage.total > actionsPage.items.length
  const {data: searchIds} = useActionsSearchIds(
    canOperate && state?.assessment && !flattened ? businessId : undefined,
    viewedYear,
    search
  )
  const {data: flatPageData} = useActionsFlat(
    canOperate && state?.assessment && flattened ? businessId : undefined,
    viewedYear,
    {
      page: flatPage,
      search,
      sortBy: ordenEfectivo === 'agrupado' ? 'target_date' : ordenEfectivo,
      sortDir: ordenEfectivo === 'agrupado' ? 'asc' : ORDER_SORT_DIR[ordenEfectivo],
      onlyPendientes: statusFilter === 'pendientes',
      fuente,
      mes,
    }
  )
  const fase2 = !!assessment?.autoevaluacion_completed_at
  // Operar el plan no exige capacidad de firma, a diferencia de los controles de atestación del checklist.
  const editable = !!assessment && !assessment.is_closed && !godReadOnly && canOperate
  // La eficacia no cuelga de `editable`: vale sobre el año cerrado y es acto de atestación, así que la
  // puerta es `sgsst_firmar`.
  const eficaciaEditable = !!assessment && !godReadOnly && capabilities.sgsst_firmar
  const {data: propuestaPreview} = usePlanPropuesta(editable && fase2 ? businessId : undefined)
  const {data: analisisSugerencias} = useAnalisisSugerencias(
    canOperate && state?.assessment ? businessId : undefined,
    viewedYear
  )
  const proposalCount = propuestaPreview?.total ?? 0
  const propuestaHref = `/businesses/${businessId}/sgsst/plan/propuesta`
  // Se publica una sola vez por llegada: `location.key` cambia con cada navegación.
  const location = useLocation()
  const confirmState = (location.state ?? null) as {created?: number; skipped?: number} | null
  const tab = parsePlanTab(searchParams.get('tab'))
  function setTab(next: PlanTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }
  const {data: resumen} = usePlanResumen(
    canOperate && state?.assessment && tab === 'resumen' ? businessId : undefined,
    viewedYear
  )

  // El dinero se calcula en el servidor (`exposure.slices`); el plan solo lo reparte y lo pinta.
  const base = state?.exposure?.base ?? null
  const shareByStandard = useMemo(() => {
    const m = new Map<string, number>()
    for (const slice of state?.exposure?.slices ?? []) m.set(slice.standard_code, slice.weight_share)
    return m
  }, [state])

  const itemByCode = useMemo(() => {
    const m = new Map<string, SgsstItem>()
    for (const item of assessment?.items ?? []) m.set(item.code, item)
    return m
  }, [assessment])

  const groups = useMemo<PlanGroup[]>(() => {
    if (!assessment) return []
    const grouped = new Map<string | null, SgsstAction[]>()
    for (const action of actions) {
      const key = action.standard_code
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key)!.push(action)
    }
    const codes = [...grouped.keys()].filter((code): code is string => code !== null)
    codes.sort((a, b) => {
      const pa = a.split('.').map(Number)
      const pb = b.split('.').map(Number)
      return pa[0] - pb[0] || pa[1] - pb[1] || pa[2] - pb[2]
    })
    const result: PlanGroup[] = codes.map((code) => ({
      code,
      item: itemByCode.get(code) ?? null,
      actions: grouped.get(code)!,
    }))
    if (grouped.has(null)) result.push({code: null, item: null, actions: grouped.get(null)!})
    return result
  }, [assessment, actions, itemByCode])

  // Precalculado sobre el conjunto completo para que la vista plana muestre la misma cifra que la agrupada.
  const perActionRiskByStandard = useMemo(() => {
    const m = new Map<string | null, Cop | null>()
    for (const group of groups) m.set(group.code, computeGroupRisk(group, base, shareByStandard).perAction)
    return m
  }, [groups, base, shareByStandard])

  const sortedGroups = useMemo(() => {
    if (sortMode === 'estandar') return groups
    return [...groups].sort((a, b) => {
      if (a.code === null) return 1
      if (b.code === null) return -1
      return (
        computeGroupRisk(b, base, shareByStandard).fullShare.high -
        computeGroupRisk(a, base, shareByStandard).fullShare.high
      )
    })
  }, [groups, sortMode, base, shareByStandard])

  const displayGroups = useMemo(() => {
    if (statusFilter === 'todas') return sortedGroups
    return sortedGroups
      .map((group) => ({...group, actions: group.actions.filter((a) => a.status !== 'cumplida')}))
      .filter((group) => group.actions.length > 0)
  }, [sortedGroups, statusFilter])

  // Mientras `searchIds` carga se muestra todo en vez de destellar una página vacía.
  const visibleIds = search && searchIds ? searchIds : null
  const renderedGroups = useMemo(() => {
    if (!visibleIds) return displayGroups
    return displayGroups.filter((group) => group.actions.some((a) => visibleIds.has(a.id)))
  }, [displayGroups, visibleIds])

  const planOpen = useMemo(
    () =>
      groups.reduce((acc, group) => addCop(acc, computeGroupRisk(group, base, shareByStandard).openShare), ZERO_COP),
    [groups, base, shareByStandard]
  )

  useEffect(() => {
    if (!hash || groups.length === 0) return
    document.getElementById(hash.slice(1))?.scrollIntoView({block: 'start'})
  }, [hash, groups])

  useHighlightParam('accion', actions.length > 0)

  const {created, skipped} = confirmState ?? {}
  useEffect(() => {
    if (created === undefined) return
    publicarExito(
      t('plan.propuesta.createdBanner', {count: created}),
      (skipped ?? 0) > 0 ? t('plan.propuesta.skippedNote', {count: skipped}) : t('plan.propuesta.createdDetalle'),
      {
        testId: 'propuesta-created-banner',
        accion: {
          etiqueta: t('plan.propuesta.verEnElPlan'),
          alActivar: () => document.getElementById('plan-lista')?.scrollIntoView({block: 'start'}),
        },
      }
    )
  }, [created, skipped, t])

  const guard = sgsstPageGuard(loading, !!business && canOperate, state)
  if (guard !== null || !state) return guard

  if (!capabilities.can_execute_plan) {
    return <PlanLockedPage feature={t('plans.feature.plan')} businessId={businessId} businessName={business?.name} />
  }

  const done = actions.filter((a) => a.status === 'cumplida').length
  const anyNoCumple = assessment?.items.some((item) => item.status === 'no_cumple') ?? false
  const assessmentHref = assessment
    ? `/businesses/${businessId}/sgsst?year=${assessment.period_year}`
    : `/businesses/${businessId}/sgsst`

  function openCreate(item: SgsstItem | null, titulo?: string) {
    setEditing(null)
    setFormStandard(item)
    setFormSuggestedTitulo(titulo ?? null)
    setFormOpen(true)
  }

  function openEdit(action: SgsstAction, item: SgsstItem | null) {
    setEditing(action)
    setFormStandard(item)
    setFormSuggestedTitulo(null)
    setFormOpen(true)
  }

  function resolve(code: string) {
    setPageError(null)
    resolveItem.mutate(code, {
      onSuccess: () => navigate(`${assessmentHref}#std-${code}`),
      onError: (err) => setPageError(translateApiError(err)),
    })
  }

  return (
    <div className="space-y-5">
      <PlanEncabezado
        businessId={businessId!}
        assessment={assessment}
        assessmentHref={assessmentHref}
        propuestaHref={propuestaHref}
        editable={editable}
        fase2={fase2}
        proposalCount={proposalCount}
        truncated={actionsTruncated ? {shown: actions.length, total: actionsPage!.total} : null}
      />

      <PlanSugerenciasNotices
        businessId={businessId!}
        sugerencias={analisisSugerencias ?? []}
        editable={editable}
        onAnalizar={(id, descripcion) => setAnalizando({id, descripcion})}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as PlanTab)}>
        <TabsList>
          <TabsTrigger value="plan" data-testid="tab-plan">
            {t('plan.tabs.plan')}
          </TabsTrigger>
          <TabsTrigger value="resumen" data-testid="tab-resumen">
            {t('plan.tabs.resumen')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="pt-4">
          {/* El año del resumen sale del periodo que se está viendo, nunca del reloj del navegador. */}
          {assessment && (
            <PlanResumenGerencial
              resumen={resumen}
              businessId={businessId!}
              planYear={viewedYear ?? assessment.period_year}
              onFiltrar={setFiltro}
            />
          )}
        </TabsContent>

        <TabsContent value="plan" className="space-y-5 pt-4">
          {actions.length > 0 && (
            <PlanCifras done={done} total={actions.length} exposicion={base && planOpen.high > 0 ? planOpen : null} />
          )}

          {actions.length > 0 && (
            <PlanMando
              search={searchInput}
              onSearch={updateSearch}
              order={ordenEfectivo}
              onOrder={chooseOrder}
              puedeAgrupar={!filtrado}
              flattened={flattened}
              sortMode={sortMode}
              onSortMode={setSortMode}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
              fuente={fuente}
              mes={mes}
              periodYear={assessment!.period_year}
              onClearFuente={() => setFiltro({})}
              onClearMes={() => setFiltro({fuente: fuente ?? undefined})}
            />
          )}

          {pageError && <p className="text-sm text-destructive">{pageError}</p>}

          <div id="plan-lista" />
          {groups.length === 0 ? (
            <PlanVacio
              assessment={assessment}
              anyNoCumple={anyNoCumple}
              fase2={fase2}
              editable={editable}
              proposalCount={proposalCount}
              propuestaHref={propuestaHref}
              assessmentHref={assessmentHref}
            />
          ) : flattened ? (
            <PlanFlatList
              businessId={businessId!}
              data={flatPageData}
              page={flatPage}
              onPageChange={setFlatPage}
              planYear={assessment!.period_year}
              editable={editable}
              itemByCode={itemByCode}
              perActionRiskByStandard={perActionRiskByStandard}
              onEdit={openEdit}
              onDelete={setDeleting}
              onOpenNotes={setNotesAction}
            />
          ) : displayGroups.length === 0 ? (
            <PlanTarjetaVacia title={t('plan.filter.noneOpen')} />
          ) : renderedGroups.length === 0 ? (
            <PlanTarjetaVacia title={t('plan.search.noMatches')} />
          ) : (
            <PlanListaAgrupada
              grupos={renderedGroups}
              hayTransversal={groups.some((g) => g.code === null)}
              base={base}
              shareByStandard={shareByStandard}
              visibleIds={visibleIds}
              resolvePending={resolveItem.isPending}
              onResolve={resolve}
              businessId={businessId!}
              assessmentHref={assessmentHref}
              planYear={assessment!.period_year}
              editable={editable}
              fase2={fase2}
              onCreate={openCreate}
              onEdit={openEdit}
              onDelete={setDeleting}
              onOpenNotes={setNotesAction}
            />
          )}
        </TabsContent>
      </Tabs>

      <PlanDialogos
        businessId={businessId!}
        editable={editable}
        eficaciaEditable={eficaciaEditable}
        canOperate={canOperate && !godReadOnly}
        formOpen={formOpen}
        onFormOpenChange={(open) => {
          setFormOpen(open)
          if (!open) {
            setEditing(null)
            setFormSuggestedTitulo(null)
          }
        }}
        formStandard={formStandard}
        editing={editing}
        suggestedTitulo={formSuggestedTitulo}
        roster={roster ?? []}
        evidence={yearEvidence?.items ?? []}
        notesAction={notesAction}
        onNotesAction={setNotesAction}
        analizando={analizando}
        onAnalizando={setAnalizando}
        deleting={deleting}
        onDeleting={setDeleting}
        onDeleteError={setPageError}
      />
    </div>
  )
}
